import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CustomerFormTypes } from "models/formTypes";
import { DialogEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import ReferencePersonFormContainer from "./forms/referencePersonFormContainer";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

export interface ReferencePersonDialogOptions {
  customerId?: number;
  /** The person to edit. Leaving it out means "add a new one". */
  person?: WintSuperkollAPIModel.Reference;
  /** Pre-fills the name when adding — e.g. text typed into a freeSolo reference-person field. */
  newName?: string;
  onSaved: (values: CustomerFormTypes.ReferencePerson) => void | Promise<void>;
}

/**
 * The single place the invoice flows open the reference-person dialog: the create-invoice customer step
 * and Slutgranska's "Skicka som" box. Both need the same title, action and container prop set, and
 * keeping two copies of it is what let them drift apart.
 */
function useReferencePersonDialog() {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const showReferencePersonDialog = ({ customerId, person, newName, onSaved }: ReferencePersonDialogOptions) => {
    dispatch(
      displayDialog({
        isOpen: false,
        title: person
          ? `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)} ${person.name ?? ""}`
          : `${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Common.NewEntity
            )} ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Person).toLowerCase()}`,
        titleIcon: IconDefinitionConstants.WBusinessCard,
        dialogType: DialogEnums.DialogType.Content,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(
              person ? TranslationKeys.Common.Save : TranslationKeys.Common.Add
            ),
            successMessage: TranslationKeys.Invoice.ReferencePersonUpdatedSuccessMessage,
          },
        ],
        children: (
          <ReferencePersonFormContainer
            id={person?.id}
            customerId={customerId}
            name={person?.name ?? newName}
            email={person?.emailAddress}
            phoneNumber={person?.phone}
            referenceId={person?.referenceIdentifier}
            isDefault={person?.isDefault}
            handleSubmit={async (submittedValues: CustomerFormTypes.ReferencePerson) => {
              await onSaved(submittedValues);
            }}
          />
        ),
        maxWidth: "sm",
      })
    );
  };

  return { showReferencePersonDialog };
}

export default useReferencePersonDialog;
